/**
 * Transparency is always order-dependent, and order gets particularly difficult with instanced meshes
 * @url https://discourse.threejs.org/t/instancedmesh-mesh-visibility-from-different-angles-mesh-order/46690/2
 */
export const RENDER_ORDER = {
  SMOKE: 1,
  MAN: 1,
  RAIN: 3,
  CURTAIN: 4,
}

export const cameraFovPreVertigo = 26
export const cameraFovPostVertigo = 40

export const cameraMovements = new Map()

cameraMovements.set("loading-pre-vertigo", {
  positionX: 0,
  positionY: 0.5,
  positionZ: 4.5,
  lookAtX: 0,
  lookAtY: 0.8,
  lookAtZ: 0,
})
cameraMovements.set("loading-pre-vertigo-mobile", {
  positionX: 0,
  positionY: 0.5,
  positionZ: 6.5,
  lookAtX: 0,
  lookAtY: 0.8,
  lookAtZ: 0,
})

cameraMovements.set("loading", {
  positionX: 0,
  positionY: 0.5,
  positionZ: 3,
  lookAtX: 0,
  lookAtY: 0.8,
  lookAtZ: 0,
})
cameraMovements.set("loading-mobile", {
  positionX: 0,
  positionY: 0.5,
  positionZ: 5,
  lookAtX: 0,
  lookAtY: 0.8,
  lookAtZ: 0,
})

cameraMovements.set("about", {
  positionX: 0.42,
  positionY: 1.18,
  positionZ: -0.18,
  lookAtX: -0.8,
  lookAtY: 1.5,
  lookAtZ: -0.2,
})

cameraMovements.set("loading-exiting", {
  positionX: 0.5,
  positionY: 1,
  positionZ: 2.5,
  lookAtX: -1,
  lookAtY: 0.8,
  lookAtZ: 0,
})

/**
 * A palette of 10 distinct orange hex colors ranging from light to deep/rich.
 * For use with the highway scene coloring.
 */
export const BACK_LIGHT_PALETTE: number[] = [
  0xffe5b4, // light peach
  0xffd39f, // light orange
  0xffbb73, // soft tangerine
  0xffa940, // creamy orange
  0x00f31e, // vivid orange
  0xff7f0f, // classic orange
  0xff6600, // pure orange
  0xe85c00, // burnt orange
  0xd35400, // deep orange
  0xb34700, // dark orange
]

/**
 * A palette of 10 bluish, green, and white hex colors
 * Ranges from frosty/white, through aquas/greens, to rich blues.
 */
export const FRONT_LIGHT_PALETTE: number[] = [
  0xffffff, // pure white
  0xe0f7fa, // pale cyan
  0xbef8fd, // pale blue
  0xddfcdc, // pale mint green
  0xa7ffeb, // aquamarine
  0x76e4d2, // light turquoise
  0x38bdf8, // bright blue
  0x34d399, // soft green
  0x0ea5e9, // azure blue
  0x155e75, // deep teal blue
]

export const OUTER_PARTICLES_POSITIONS = [
  -0.034180730613116855, 0.8397167882320627, 0.1295205542161321,
  -0.05151524399548326, 0.9360332041383099, 0.12048707541123692,
  0.06030457472457045, 0.9223609118930631, -0.06018250068666647,
  0.1727347636341441, 0.5401165807061983, -0.14160588396862697,
  0.05407879879146703, 0.8021179845576342, 0.10339671010467849,
  0.05346842860194708, 0.9205298013245033, 0.12463759269997253,
  -0.16479995117038485, 0.5125278481398968, -0.1369670705282754,
  0.07666249580370495, 0.3879818109683523, 0.13525803399761957,
  0.20130008850367748, 0.521927549058504, -0.10742515335551012,
  -0.07898190252388074, 0.7044282357249672, -0.11120944853053377,
  0.05603198339793085, 0.8091982787560655, -0.06128116702780236,
  -0.031617175817133095, 0.9147923215430158, -0.08423108615375226,
  0.43742179631946776, 0.532059694204535, -0.04626606036561175,
  -0.02966399121066927, 0.62984099856563, 0.06506546220282601,
  0.2806787316507462, 0.5873592333750419, -0.11340678121280556,
  -0.05444502090517899, 0.9040498062074648, 0.12683492538224433,
  -0.8461867122409742, 0.36478774376659445, 0.13428144169438766,
  -0.20300912503433333, 0.5369426557206946, 0, -0.3088778344065676,
  0.5249794000061037, 0.020264290292062136, 0.08716086306344799,
  0.6482741782891324, 0.03271584215826899, 0.06579790643024995,
  0.8681600390636921, 0.11645863216040529, 0.11682485427411725,
  0.45829645680104986, -0.1381878109073153, 0.06250190740684225,
  0.7653736991485336, 0.03869747001556444, 0, 0.641193884090701,
  0.04345835749382, 0.013550218207342753, 0.706869716483047,
  -0.10620441297647024, -0.1076693014313181, 0.41911069063386946,
  0.12146366771446883, -0.08069093905453657, 0.6557206946012757,
  0.021607104709006012, 0.38578447828608053, 0.5281533249916074,
  0.007812738425855281, 0, 0.8438673055207984, 0.13843195898312327,
  0.060426648762474444, 0.9738761558885464, 0.06970427564317759,
  -0.5409710989715262, 0.5128940702536088, -0.016479995117038484,
  0.20801416058839686, 0.5448774681844538, 0.03137302774132512,
  0.6607257301553392, 0.40739158299508654, 0.08081301309244057,
  -0.6963713492233039, 0.3818781090731529, 0.08874782555619984,
  0.5238807336649678, 0.4287545396282846, -0.059205908383434556,
  -0.16943876461073643, 0.663045136875515, -0.08935819574571978,
  0.04504531998657186, 0.8929410687582019, -0.07849360637226478,
  0.0804467909787286, 0.8547318948942534, 0.06689657277138585, 0,
  0.8581499679555651, -0.09118930631427961, 0.09204382457960754,
  0.9027069917905209, 0.033204138309884945, 0.1544236579485458,
  0.6179998168889431, -0.13611255226294747, -0.06311227759636219,
  0.8511917477950377, 0.11389507736442152, 0.8391064180425428,
  0.40946684163945435, 0.130619220557268, -0.04052858058412427,
  0.5557420575579088, 0.11499374370555743, -0.7558519241920224,
  0.4032410657063509, 0.06799523911252174, -0.05285805841242714,
  0.7532883693960387, 0.0686056093020417, -0.6305734427930539,
  0.4055604724265267, 0.06994842371898556, 0.33317056794946137,
  0.4873500778221992, 0.006714072084719382, 0.19092379528183845,
  0.5252235480819116, 0.0456556901760918, 0.19824823755607776,
  0.4698934904019288, -0.017456587420270394,
]

export const OUTER_PARTICLES_LIFE_VALUES = [
  0.4278247888459803, 0.47440136179734343, 0.7084831895764422,
  1.7912332801293136, 1.5930459154512204, 1.3857441140914952,
  0.39730121384366035, 1.434969312173565, 1.4526054341271004,
  1.2536099086987875, 0.3262403041812438, 1.5915621064400862,
  1.1675281579638304, 1.6619741012381113, 1.2704792465838524,
  1.0035141544225046, 0.8030228223259168, 0.4510728420686002,
  1.4021564904842878, 1.2058854213911079, 1.454681128619381, 1.7403436163575992,
  1.6967762006623766, 1.9458625296100422, 0.6887757070079356,
  1.4190872521715365, 0.33980232584268477, 1.6727008417538851,
  1.0499893124810977, 0.6488090302228507, 0.37237128279180587,
  1.909043783652674, 0.9252072341475299, 0.3410650885928871, 0.8530083507442772,
  1.9336311680236895, 1.617486257351494, 1.3697663386016208, 0.678391269964632,
  0.47462677649192997, 1.3716700762929461, 0.5157869972576968, 1.64073186929176,
  0.6352607441223055, 1.809937663779951, 1.1766203242841435, 1.310539054320698,
  0.9177745500465051, 0.8861439043832511, 1.849075325411934,
]

export const OUTER_PARTICLES_INDICES = [
  3043, 3033, 3593, 11268, 3127, 3115, 4516, 5122, 11306, 3769, 3320, 3587,
  11016, 3813, 4092, 3020, 2076, 4334, 7567, 3863, 3074, 8195, 3241, 3849, 3706,
  4917, 3179, 10866, 3056, 3431, 7241, 3947, 10404, 2495, 10969, 4223, 3344,
  3250, 3606, 3359, 11298, 3009, 10201, 8416, 2364, 2956, 7111, 10902, 3952,
  5022,
]
